/**
 * VITAL_CONFIG — industry-standard vital sign definitions.
 * LOINC codes sourced from loinc.org (used by Epic, Cerner, Apple Health).
 * Normal ranges are general adult population defaults; individual baselines may differ.
 */
export const VITAL_CONFIG = {
  blood_pressure: {
    loinc:    '55284-4',
    label:    'Blood Pressure',
    unit:     'mmHg',
    // BP is "systolic/diastolic" — evaluated separately
    normalRange: null,
    // Systolic thresholds (mmHg)
    thresholds: {
      low:      { systolic: [0, 89],    diastolic: [0, 59] },
      normal:   { systolic: [90, 120],  diastolic: [60, 80] },
      elevated: { systolic: [121, 129], diastolic: [60, 80] },
      high:     { systolic: [130, 999], diastolic: [80, 999] },
    },
  },
  heart_rate: {
    loinc:       '8867-4',
    label:       'Heart Rate',
    unit:        'bpm',
    normalRange: [60, 100],
  },
  oxygen_saturation: {
    loinc:       '59408-5',
    label:       'Oxygen Saturation',
    unit:        '%',
    normalRange: [95, 100],
  },
  temperature: {
    loinc:       '8310-5',
    label:       'Body Temperature',
    unit:        '°C',
    normalRange: [36.1, 37.2],
  },
  weight: {
    loinc:       '29463-7',
    label:       'Body Weight',
    unit:        'kg',
    normalRange: null, // context-dependent — no universal threshold
  },
  blood_glucose: {
    loinc:       '2339-0',
    label:       'Blood Glucose',
    unit:        'mg/dL',
    normalRange: [70, 99], // fasting
  },
  respiratory_rate: {
    loinc:       '9279-1',
    label:       'Respiratory Rate',
    unit:        'breaths/min',
    normalRange: [12, 20],
  },
};

/**
 * Returns { status: 'normal'|'low'|'elevated'|'high'|'unknown', label, color }
 * for a given vital type + value string.
 */
export function getVitalStatus(type, valueStr) {
  const cfg = VITAL_CONFIG[type];
  if (!cfg) return { status: 'unknown', label: '—', color: 'bg-slate-100 text-slate-500' };

  if (type === 'blood_pressure') {
    const parts = valueStr?.split('/').map(Number);
    if (!parts || parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) {
      return { status: 'unknown', label: '—', color: 'bg-slate-100 text-slate-500' };
    }
    const [sys, dia] = parts;
    if (sys >= 130 || dia >= 80) return { status: 'high',     label: 'High',     color: 'bg-red-50 text-red-600 border border-red-100' };
    if (sys >= 121)              return { status: 'elevated',  label: 'Elevated', color: 'bg-amber-50 text-amber-600 border border-amber-100' };
    if (sys >= 90 && dia >= 60)  return { status: 'normal',    label: 'Normal',   color: 'bg-emerald-50 text-emerald-600 border border-emerald-100' };
    return                              { status: 'low',       label: 'Low',      color: 'bg-blue-50 text-blue-600 border border-blue-100' };
  }

  if (!cfg.normalRange) return { status: 'unknown', label: '—', color: 'bg-slate-100 text-slate-500' };

  const val = parseFloat(valueStr);
  if (isNaN(val)) return { status: 'unknown', label: '—', color: 'bg-slate-100 text-slate-500' };

  const [min, max] = cfg.normalRange;
  if (val < min) return { status: 'low',    label: 'Low',    color: 'bg-blue-50 text-blue-600 border border-blue-100' };
  if (val > max) return { status: 'high',   label: 'High',   color: 'bg-red-50 text-red-600 border border-red-100' };
  return               { status: 'normal',  label: 'Normal', color: 'bg-emerald-50 text-emerald-600 border border-emerald-100' };
}

/** Human-readable label for a vital type key. */
export const vitalLabel = (type) => VITAL_CONFIG[type]?.label ?? type.replace(/_/g, ' ');

/** Default unit for a vital type. */
export const vitalUnit = (type) => VITAL_CONFIG[type]?.unit ?? '';

export const SOURCE_LABELS = {
  self_reported:  { label: 'Self-reported', color: 'bg-slate-100 text-slate-500' },
  clinician:      { label: 'Clinician',     color: 'bg-blue-50 text-blue-600' },
  device:         { label: 'Device',        color: 'bg-violet-50 text-violet-600' },
  emr:            { label: 'EMR Import',    color: 'bg-amber-50 text-amber-700' },
  // lab result sources
  doctor_entered: { label: 'Doctor entered', color: 'bg-blue-50 text-blue-600' },
  lab_imported:   { label: 'Lab import',     color: 'bg-violet-50 text-violet-600' },
};
